<script setup>
import {
  ref,
  provide,
  inject,
  onBeforeMount,
  onMounted,
  watch,
  computed,
  onBeforeUnmount,
  reactive,
  nextTick,
  toRefs,
} from 'vue'
import RenewEmployerModal from '../shared/components/RenewEmployerModal/RenewEmployerModal.vue'
import BcsModal from '@/shared/components/BcsModal.vue'
import { useNavigationStore } from '../shared/stores/NavigationStore'
import { RemindersCalendarService } from '../shared/services/RemindersCalendarService'
import { RequestService } from '../shared/services/RequestService'
import { v4 as uuidv4 } from 'uuid'

import { formatDateToSendReminder, getTodayDate } from '../shared/components/Reminders/dateUtils'
import { withEmployerNameInInvite } from '../shared/components/Reminders/helpers'
import ExistingEmployerDashboard from '../shared/components/ExistingEmployerDashboard/ExistingEmployerDashboard.vue'
import BcsFaqs from '../shared/components/BcsFaqs.vue'
import BcsGlossary from '../shared/components/BcsGlossary.vue'
import ContentLibraryEmbeddedTool from '../shared/components/ContentLibraryEmbeddedTool.vue'
import BcsContentLibrary from '../shared/components/BcsContentLibrary.vue'
import BCSContentLibraryTaxonomyItems from './BCSContentLibraryTaxonomyItems.vue'
import HelpAndResources from '../shared/components/HelpAndResources.vue'
import TransformLearningDashboard from '../shared/components/TransformLearning'
import TransformModuleDetail from '../shared/components/TransformLearning/TransformModuleDetail.vue'
import TransformLessonDetail from '../shared/components/TransformLearning/TransformLessonDetail.vue'
import TransformSearchResults from '../shared/components/TransformLearning/TransformSearchResults.vue'
import TransformQuizResultsBreakdown from '../shared/components/TransformLearning/TransformQuizResultsBreakdown.vue'
import ActionCenter from '../shared/components/ActionCenter/ActionCenter.vue'
import AdminCenter from '../shared/components/AdminCenter/AdminCenter.vue'
import Footer from '../shared/components/FooterSection.vue'
import Faqs from '../shared/components/FaqsSection.vue'
import BcsDynamicText from '../shared/components/BcsDynamicText.vue'
import { storeToRefs } from 'pinia'
import { getAccountOwners } from '../shared/utils/accountOwnersHelpers'
import Offcanvas from '../shared/components/OffCanvas.vue'
import ActionCenterOffCanvas from '../shared/components/ActionCenter/RequirementsCompletion/ActionCenterOffCanvas.vue'
import CenterDefinitionModal from '../shared/components/common/CenterDefinitionModal.vue'
import { useQuestionsStore } from '../shared/stores/QuestionsStore'
import { useRenewalEmployerStore } from '../shared/stores/RenewalEmployerStore'
import { useEmployerStore } from '../shared/stores/EmployerStore'
import { useBusinessDaysReminder } from '../shared/composables/useBusinessDaysReminder'
import { useSessionStorageStore } from '../shared/stores/SessionStorage'
import TermsAndConditions from '../shared/components/TermsAndConditions.vue'
import { useGlossaryStore } from '../shared/stores/GlossaryStore'
import brandLogo from '@/assets/images/BCS-Branding.svg'
import {
  getDatabase,
  ref as dbRef,
  get as dbGet,
  set as dbSet
} from 'firebase/database'
import { getApp } from 'firebase/app'
import {
  ACTIVITY_NEWEST_TO_OLDEST,
  ACTIVITY_OLDEST_TO_NEWEST,
  formatDateToSQLInChicagoTime,
  ORDER_A_TO_Z,
  ORDER_NEWEST_TO_OLDEST,
  ORDER_OLDEST_TO_NEWEST,
  ORDER_Z_TO_A
} from '../shared/utils'
import { useAuthStore } from '../shared/stores/AuthStore'
import { PERMISSION_KEYS } from '../shared/access/permissionKeys'
import { useAppCapabilities } from '../shared/composables/useAppCapabilities'
import { API_CAPABILITY } from '../shared/access/apiCapabilities'
import { usePermissionGatedNavigation } from '../shared/composables/usePermissionGatedNavigation'
import BcsMemberProfile from '../shared/components/Profile/BcsMemberProfile.vue'
import { getAuth } from 'firebase/auth'
import { FirebaseStorageService } from '../shared/services/FirebaseStorageService'
import BcsContactModal from '../shared/components/Contact/BcsContactModal.vue'
import BcsSearchDropdown from '../shared/components/BcsSearchDropdown.vue'
import RecentLibraryUpdates from '../shared/components/RecentLibraryUpdates.vue'
import LatestActivityTable from '../shared/components/LatestActivityTable.vue'
import LimitedAccessBadge from '../shared/components/LimitedAccessBadge.vue'
import * as bootstrap from 'bootstrap'
import NavBar from '../shared/components/common/NavBar.vue'
import MobileMessage from './MobileMessage.vue'
import BcsTooltip from '@/shared/components/BcsTooltip.vue'
import AlertNotification from '../shared/components/AlertNotification.vue'
import { get } from 'lodash'
import { PLAN_YEAR_EVENTS } from '../shared/utils/planYearEvents'
// import { ScoreCalculationsService } from '../shared/services/ScoreCalculationsService'
import CreateUpdateEmployerModal from '../shared/components/CreateUpdateEmployerModal.vue'
import { addUpcomingEventsToEmployers } from '../shared/utils/employersHelpers' // Helper to enrich employer objects
import { AgencyDataService } from '../shared/services/AgencyDataService'
import ReminderModalVue from '../shared/components/Reminders/ReminderModal.vue'
import { useTemplateVariablesStore } from '../shared/stores/TemplateVariablesStore'
import { useLogout } from '../shared/composables/useLogout'
import { useBrowserHistoryBridge } from '../shared/composables/useBrowserHistoryBridge'
import megaphoneIcon from '@/assets/images/megaphone-announcement.svg'
// import { ContentLibraryService } from '../shared/services/ContentLibraryService'

const auth = getAuth()
const navigationStore = useNavigationStore()
const authStore = useAuthStore()
const { customNavigation } = storeToRefs(navigationStore)
const { isAccessLoaded, hasCoreWorkbench, showFullWorkbenchHome, showLibraryStyleHome, isContentLibraryOnlyUser, hasContentLibrary, hasTransform, canCallApi } = useAppCapabilities()

/*
 * Which FAQ sections the Home preview shows. Each persona only ever sees the modules it
 * holds — a transform-only user is never served the general or Content Library questions,
 * and a user with both gets two of each. Section ids live on the FAQS node in Realtime
 * Database (see FaqsStore).
 */
const homeFaqSections = computed(() => {
  if (!showLibraryStyleHome.value) return 'general'
  return [
    ...(hasContentLibrary.value ? ['contentLibrary'] : []),
    ...(hasTransform.value ? ['transform'] : [])
  ]
})
const { navbarLinks } = usePermissionGatedNavigation(customNavigation)
// const contentLibraryService = ContentLibraryService()
const { refNavigate } = storeToRefs(navigationStore)
const { getImageUrl } = FirebaseStorageService()
const functionsBaseUrl = inject('functionsBaseUrl')

provide('customNavigation', customNavigation)
// Bridges browser Back/Forward to the virtual router, with symmetric listener teardown on
// unmount (prevents the leak that made a single Back fire goBack() multiple times across remounts).
useBrowserHistoryBridge(customNavigation)
const db = getDatabase(getApp())
const uid = localStorage.getItem('userUid')

const { postReminders, getEmails } = RemindersCalendarService()

const userMetadata = ref({})
const allEmployersList = ref([])
const isLoggingOut = ref(false)
const searchActive = ref(false)
const searchResults = ref([])
const isMobile = ref(false)
const showAnnouncement = ref(false)
const showFeatureAnnouncement = ref(false)
const CreateUpdateEmployerModalRef = ref(null)
const reminderSentModalRef = ref(null)
const activeTooltipPlanYearId = ref(null)


const offcanvasRef = ref(null)
const actionCenterOffCanvasRef = ref(null)
const selectedContext = ref({})
const homeReminderModalRef = ref(null)
const reminderModalProps = reactive({
  eventDefinition: {},
  actualEmployer: {},
  actualPlanYear: {},
  recipients: [],
  yearOfActualPlan: ''
})

const { 
  eventDefinition: eventDefinitionRef, 
  actualPlanYear: actualPlanYearRef, 
  yearOfActualPlan: yearOfActualPlanRef 
} = toRefs(reminderModalProps)

const { reminderDaysOption, subtractBusinessDays } = useBusinessDaysReminder(eventDefinitionRef, actualPlanYearRef, yearOfActualPlanRef)
const templateStore = useTemplateVariablesStore()
const { logout: performLogout } = useLogout()
const { getDefinitionBodySync } = templateStore

const orderOptions = [
  {
    label: 'Activity Newest to Oldest',
    value: ACTIVITY_NEWEST_TO_OLDEST,
    sortDirection: 'DESC',
    sortField: 'log'
  },
  {
    label: 'Activity Oldest to Newest',
    value: ACTIVITY_OLDEST_TO_NEWEST,
    sortDirection: 'ASC',
    sortField: 'log'
  },
  { label: 'Employer Name A-Z', value: ORDER_A_TO_Z, sortDirection: 'ASC', sortField: 'name' },
  { label: 'Employer Name Z-A', value: ORDER_Z_TO_A, sortDirection: 'DESC', sortField: 'name' },
  {
    label: 'Date Added Newest to Oldest',
    value: ORDER_NEWEST_TO_OLDEST,
    sortDirection: 'DESC',
    sortField: 'creation_date'
  },
  {
    label: 'Date Added Oldest to Newest',
    value: ORDER_OLDEST_TO_NEWEST,
    sortDirection: 'ASC',
    sortField: 'creation_date'
  }
]

const { createLog, simpleGet, handleErrors } = RequestService()

const emitter = inject('emitter')

// Close the create/update employer modal when opening the renew employer modal, with cleanup
const hideRenewModalOnOpenHandler = () => {
  if (CreateUpdateEmployerModalRef.value) {
    CreateUpdateEmployerModalRef.value.closeModal()
  }
}
emitter.on('open-renew-employer-modal', hideRenewModalOnOpenHandler)

emitter.on('action-center:open-action-items-offcanvas', (payload) => {
  if (actionCenterOffCanvasRef.value) {
    actionCenterOffCanvasRef.value.open(payload)
  }
})

const employerStore = useEmployerStore()
const sessionStorageStore = useSessionStorageStore()
const glossaryStore = useGlossaryStore()

const { actualEmployer, actualPlanYear, userData, allDistinctEmployers } = storeToRefs(employerStore)
const { getEvent, setEvent, cleanEvent, cleanAllEvents } = sessionStorageStore
const {
  getAllDistinctEmployers,
  createEmployer,
  resetAll: resetAllEmployerStore,
  updateEmployer,
  changePlanStartDateCopy
} = employerStore

const { fetchGlossaryData } = glossaryStore


const checkMobileView = () => {
    isMobile.value = window.innerWidth <= 1000
}

const profilePhoto = computed(() =>
  userData.value?.temporaryPhotoURL !== '/user_silhouette.png' && userData.value?.temporaryPhotoURL !== undefined
    ? userData.value.temporaryPhotoURL
    : userData.value?.profilePhotoUrl
      ? userData.value.profilePhotoUrl
      : userData.value?.logoPath
        ? userData.value.logoPath
        : null
)

const getCurrentUserMetadata = () => {
  if (auth.currentUser) {
    userMetadata.value = {
      creationTime: auth.currentUser?.metadata?.creationTime,
      lastLogin: auth.currentUser?.metadata?.lastSignInTime
    }
  }
}

const hasEmptyValues = ref(true)

const logout = async () => {
  isLoggingOut.value = true
  await performLogout()
}
const profilePhotoUrl = ref('')
const questionStore = useQuestionsStore()
const renewalStore = useRenewalEmployerStore()
// const { reminderDaysOption } = useBusinessDaysReminder() // Invoke the composable
const { resetQuestionStore } = questionStore
const { resetRenewalStore } = renewalStore
// Form fields as simple refs since validation is handled by EmployerBasicInfoForm
const name = ref('')
const accountOwners = ref([])
const ein = ref('')
const plan_start_date = ref('')
const contact_name = ref('')
const contact_emails = ref('')
//const customNavigation = inject('customNavigation')
const employerData = computed(() => ({
  name: name.value,
  accountOwners: accountOwners.value,
  ein: ein.value,
  plan_start_date: plan_start_date.value,
  contact_name: contact_name.value,
  contact_emails: contact_emails.value
}))
const employerList = ref([])
const employerTotalCount = ref(0)
// const latestLibraryActivities = ref([])

const employerAmountToShow = ref(10)
const inProgress = ref(0)

// Loading overlay shows while operations are in flight, or while home waits for permissions.
const loadingCount = ref(0)
const isOnHomePage = computed(() => (
  customNavigation.value.currentPage === customNavigation.value.routes.Home.name
))
const loading = computed(() => (
  loadingCount.value > 0 || (isOnHomePage.value && !isAccessLoaded.value)
))

function pushLoading() {
  loadingCount.value++
}

function popLoading() {
  if (loadingCount.value > 0) {
    loadingCount.value--
  }
}

const forceUpdate = ref(false)
const messageForLoading = ref(null)
const orderBySelected = ref(orderOptions[4])
const renewEmployerModalRef = ref(null)
const searchDropdownRef = ref(null)
const checkDuplicateEmployer = ref(false)
const duplicateEmployerMatch = ref(null)
const isNewEmployerAdded = ref({
  state: false,
  id: null
})
const isSubmitting = ref(false)

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

const handleTooltipToggle = (status, planYearId) => {
  if (status === true) {
    activeTooltipPlanYearId.value = planYearId
  } else if (status === null) {
    activeTooltipPlanYearId.value = null
  }
}

const handleEventClicked = async (context) => {
  selectedContext.value = context
  // We use nextTick to ensure that the 'selectedContext' prop has been passed down
  // to the OffCanvas component before we try to open it.
  await nextTick()

  if (offcanvasRef.value) {
    const { event, source } = context
    const { body, name, date } = event
    offcanvasRef.value.openOffCanvas(body, name, date, false, source, null)
  }
}

const handleOpenReminderModal = () => {
  if (selectedContext.value.event && selectedContext.value.employer) {
    const { event, employer } = selectedContext.value
    const planStartDateStr = employer.plan_start_date ?? ''
    const [dueMonth, dueDay] = event.date.split('/').map(Number)
    const [planMonth, planDay, planYearNumber] = planStartDateStr.split('/').map(Number)

    let yearOfActualPlan = planYearNumber.toString()
    if (dueMonth < planMonth || (dueMonth === planMonth && dueDay < planDay)) {
      yearOfActualPlan = (parseInt(planYearNumber) + 1).toString()
    }

    reminderModalProps.eventDefinition = event
    reminderModalProps.recipients = userData.value.agencyEmails || []
    reminderModalProps.actualEmployer = employer
    reminderModalProps.actualPlanYear = employer
    reminderModalProps.yearOfActualPlan = yearOfActualPlan

    const modalElement = document.getElementById('addDueDateModal')
    if (modalElement) {
      const modalInstance = bootstrap.Modal.getOrCreateInstance(modalElement)
      modalInstance.show()
    }

    const offcanvasElement = document.querySelector('.offcanvas.show')
    if (offcanvasElement) {
      const offcanvasInstance = bootstrap.Offcanvas.getInstance(offcanvasElement)
      offcanvasInstance?.hide()
    }
  }
}

const handleSendReminders = async (remindersData) => {
  try {
    const {
      eventDefinition,
      selectedRecipients = [],
      validEmails = [],
      selectedDays,
      modalReminderSource
    } = remindersData
    const { actualEmployer, actualPlanYear, yearOfActualPlan } = reminderModalProps

    // Only process OffCanvas flow (single reminder)
    if (modalReminderSource !== 'OffCanvas') return

    const event = eventDefinition
    let reminderDays = selectedDays

    if (event.customReminder !== null && event.customReminder !== undefined) {
      reminderDays = event.customReminder
    }

    // Format due-date to correct plan year
    const endDate = formatDateToSendReminder(
      event.date,
      actualPlanYear,
      yearOfActualPlan
    )

    /* ---------- Build attendees ---------- */
    const attendees = [
      ...selectedRecipients,
      ...validEmails
        .filter((e) => e.trim() !== '')
        .map((e) => ({ email: e.trim() }))
    ].filter((a) => a.email !== '')

    /* ---------- Build description ---------- */
    const baseDescription = getDefinitionBodySync(event.body) || event.body || ''
    const inviteBody = `<b>Due Date: ${endDate}</b>\n\n${baseDescription}`
    const completeDescription = withEmployerNameInInvite(actualEmployer.name, inviteBody)

    /* ---------- Calculate start date ---------- */
    let startDate
    if (reminderDays === 0) {
      startDate = getTodayDate()
    } else {
      const startDateObj = subtractBusinessDays(
        new Date(endDate),
        reminderDays
      )
      startDate = startDateObj.toISOString().split('T')[0]
    }

    /* ---------- Build payload ---------- */
    const body = {
      id: 'bcspro' + uuidv4().toLowerCase().replace(/-/g, ''),
      summary: event.name,
      description: completeDescription,
      start: { date: startDate },
      end: { date: startDate },
      attendees,
      dueDate: endDate,
      employerId: actualEmployer.employer_id,
      planYearId: actualPlanYear.planYearId
    }

    /* ---------- Send reminder and register log ---------- */
    await postReminders(body)

    await createLog({
      logName: 'sent_reminder_log_v2',
      employerId: actualEmployer.employer_id,
      planYearId: actualPlanYear.planYearId,
      agencyId: actualEmployer.agency_id,
      origin: 'bcs-offcanvas',
      role: userMetadata.value.role || '',
      body
    })

    const { markAgencyDataUpdated } = AgencyDataService()
    markAgencyDataUpdated(actualEmployer.agency_id)

    homeReminderModalRef.value?.clearModal()
    reminderSentModalRef.value.openModal()
  } catch (error) {
    handleErrors('handleSendReminders', error)
  }
}

function setupModalEventListeners() {
  nextTick(() => {
    const addDueDateModal = document.getElementById('addDueDateModal')
    if (addDueDateModal) {
      addDueDateModal.addEventListener('shown.bs.modal', () => {
        if (homeReminderModalRef.value) {
          homeReminderModalRef.value.insertDefaultEmails()
        }
      })

      addDueDateModal.addEventListener('hidden.bs.modal', () => {
        if (homeReminderModalRef.value) {
          homeReminderModalRef.value.clearModal()
        }
      })
    }
  })
}

const getPageTitle = (routePath) => {
  if (!routePath) return ''
  
  // If the route contains an employer ID (contains '/')
  if (routePath.includes('/')) {
    const pageParts = routePath.split('/')

    const pagePart = pageParts[pageParts.length - 1]

    // Find the subpage title in DetailEmployer children
    const childRoute = customNavigation.value.routes.DetailEmployer.children.find(
      child => child.name === pagePart
    )
    
    if (childRoute) {
      return childRoute.title
    }
  }
  
  // Search in all main routes
  for (const route of Object.values(customNavigation.value.routes)) {
    if (route.name === routePath) {
      return route.title
    }
  }
  
  return routePath
}

onBeforeMount(async () => {
  await authStore.checkAuth()

  // Check if we should show profile from sessionStorage flag
  if (sessionStorage.getItem('navigateToProfile') === 'true') {
    // Clean the flag immediately
    sessionStorage.removeItem('navigateToProfile')
    // Navigate to profile after a short delay to ensure navigation store is ready
    setTimeout(() => {
      customNavigation.value.navigate({
        newPage: customNavigation.value.routes.memberProfile.name
      })
    }, 100)
  }

  // Deep link support for Transform views: the quiz-completion email and the admin
  // "view-as" links (?view=<transformRoute>&owner=<uid>). The URL params are converted
  // into the same sessionStorage events the F5 restore reads, then the restore table
  // below rebuilds the view (falling back to the dashboard when a required id is missing).
  const urlParams = new URLSearchParams(window.location.search)
  const deepLinkView = urlParams.get('view')
  const transformDeepLinkRoutes = [
    customNavigation.value.routes.transformLearningDashboard.name,
    customNavigation.value.routes.transformModuleDetail.name,
    customNavigation.value.routes.transformLessonDetail.name,
    customNavigation.value.routes.transformSearchResults.name,
    customNavigation.value.routes.transformQuizResultsBreakdown.name
  ]
  if (transformDeepLinkRoutes.includes(deepLinkView)) {
    setEvent('route', deepLinkView)
    // Set each provided param's event; clear the ones absent from the URL so a stale
    // value from an earlier session never leaks into the deep-linked view.
    const deepLinkParamEvents = {
      lessonId: 'transformLessonId',
      lessonTitle: 'transformLessonTitle',
      moduleId: 'transformModuleId',
      moduleTitle: 'transformModuleTitle',
      searchTerm: 'transformSearchTerm',
      // Optional admin `owner` (any Transform view): enters the view-as context via the
      // restore navigate() below; cleared when absent so an own-data link exits it.
      owner: 'transformOwner'
    }
    for (const [param, eventKey] of Object.entries(deepLinkParamEvents)) {
      const value = urlParams.get(param)
      if (value) setEvent(eventKey, value)
      else cleanEvent(eventKey)
    }
    setEvent('isSubpage', false) // Force restore logic execution
  }

  const isSubpage = getEvent('isSubpage')
  const route = getEvent('route')
  const subroute = getEvent('subroute')
  const paramsId = getEvent('routeParamsId')
  const internalQuery = getRefreshInternalQuery(route)
  popLoading()
  // navigate() gates routes and filters breadcrumb ancestors by permission, and permissions
  // load async — resolve them once up front so the F5 restore, the transform-only redirect,
  // and every gated navigate() below act on the real permission set, not an empty one. A
  // gated view (e.g. Help & Resources, Admin Center) would otherwise be silently rejected and
  // gated ancestors wrongly dropped from the rebuilt breadcrumb.
  await authStore.ensureAccessLoaded()
  if (!isSubpage && isSubpage !== null) {
    // Scoped fix (Terms & Conditions only): T&C uses ${previousPage} for its
    // breadcrumb, which is lost on refresh. Replay the saved previous page
    // first so the breadcrumb rebuilds with the correct parent.
    const tcRoute = customNavigation.value.routes.termsAndConditions.name
    if (route === tcRoute) {
      const prev = getEvent('termsAndConditionsPreviousPage')
      if (prev && prev !== tcRoute) {
        customNavigation.value.navigate({
          newPage: prev,
          params: prev.startsWith('detailEmployer') && paramsId ? { planYearId: paramsId } : {}
        })
      }
    }
    // Transform routes are reconstructed from persisted events (navigationParams live only
    // in memory). Each needs one key present to rebuild; without it we fall back to the
    // dashboard. Declared as a table so adding a route is one entry, not another branch.
    const transformDashboardRoute = customNavigation.value.routes.transformLearningDashboard.name
    const transformRestoreByRoute = {
      // Dashboard needs no params (requiredEvent: null) — it just has to be in the table so
      // the F5 restore awaits ensureAccessLoaded() before navigating; otherwise the
      // permission gate rejects it and the user lands on Help & Resources instead.
      [transformDashboardRoute]: {
        requiredEvent: null,
        buildParams: () => ({})
      },
      [customNavigation.value.routes.transformSearchResults.name]: {
        requiredEvent: 'transformSearchTerm',
        buildParams: () => ({
          searchTerm: getEvent('transformSearchTerm'),
          searchOrigin: getEvent('transformSearchOrigin') ?? undefined,
          searchOriginModuleId: getEvent('transformSearchOriginModuleId') ?? undefined,
          searchOriginModuleTitle: getEvent('transformSearchOriginModuleTitle') ?? undefined
        })
      },
      [customNavigation.value.routes.transformModuleDetail.name]: {
        requiredEvent: 'transformModuleId',
        buildParams: () => ({
          moduleId: getEvent('transformModuleId'),
          moduleTitle: getEvent('transformModuleTitle') ?? undefined
        })
      },
      [customNavigation.value.routes.transformLessonDetail.name]: {
        requiredEvent: 'transformLessonId',
        buildParams: () => ({
          lessonId: getEvent('transformLessonId'),
          lessonTitle: getEvent('transformLessonTitle') ?? undefined,
          moduleId: getEvent('transformModuleId') ?? undefined,
          moduleTitle: getEvent('transformModuleTitle') ?? undefined
        })
      },
      [customNavigation.value.routes.transformQuizResultsBreakdown.name]: {
        requiredEvent: 'transformLessonId',
        buildParams: () => ({
          lessonId: getEvent('transformLessonId'),
          lessonTitle: getEvent('transformLessonTitle') ?? undefined,
          moduleId: getEvent('transformModuleId') ?? undefined,
          moduleTitle: getEvent('transformModuleTitle') ?? undefined
        })
      }
    }
    // Admin "view-as" survives F5 on every Transform view: re-inject the persisted owner
    // so the restore navigate() re-enters the context (NavigationStore seeds it from the
    // `owner` param). Applied table-wide instead of per entry — the context is module-wide.
    const persistedTransformOwner = getEvent('transformOwner')
    if (persistedTransformOwner) {
      for (const entry of Object.values(transformRestoreByRoute)) {
        const buildWithoutOwner = entry.buildParams
        entry.buildParams = () => ({ ...buildWithoutOwner(), owner: persistedTransformOwner })
      }
    }

    const transformRestore = transformRestoreByRoute[route]
    if (transformRestore) {
      // Permissions are resolved above, so navigate()'s route-level gate will pass here.
      // requiredEvent null → route needs no persisted params (e.g. the dashboard); restore
      // it directly. Otherwise require the key, falling back to the dashboard if it's gone.
      if (!transformRestore.requiredEvent || getEvent(transformRestore.requiredEvent)) {
        customNavigation.value.navigate({ newPage: route, params: transformRestore.buildParams() })
      } else {
        customNavigation.value.navigate({ newPage: transformDashboardRoute })
      }
    } else {
      navigateToCurrentRoute(route, internalQuery)
    }
  }
  if (isSubpage !== null) {
    if (isSubpage) {
      const data = {
        route,
        subroute,
        paramsId,
        query: internalQuery
      }
      await navigateCurrentSubroute(data)
      popLoading()
    }
  }
  await refreshHomeData({ showLoading: true, refreshAll: true, forceRefresh: false })
})

const getRefreshInternalQuery = (route) => {
  const taxonomyRoute = customNavigation.value.routes.bcsContentLibraryTaxonomyItems.name
  const embeddedToolRoute = customNavigation.value.routes.contentLibraryEmbeddedTool.name

  if (route === taxonomyRoute) {
    return getEvent('contentLibraryTaxonomyInternalQuery') || {}
  }

  if (route === embeddedToolRoute) {
    return getEvent('contentLibraryEmbeddedToolInternalQuery') || {}
  }

  return {}
}

const navigateToCurrentRoute = (route, query = {}) => {
  customNavigation.value.navigate({
    newPage: route,
    query
  })
}

const navigateCurrentSubroute = async (data) => {
  customNavigation.value.navigate({
    newPage: data.route,
    subPage: data.subroute,
    params: { planYearId: data.paramsId },
    query: data.query || {}
  })
}

// Handler to open modal and pre-fill form
const openCreateUpdateHandler = () => {
  if (actualEmployer.value) fillFormWithEmployerData()
  else cleanFormNewUser()
  openCreateUpdateEmployerModal()
}

const recentLibraryItems = ref([])
const latestLibraryActivities = ref([])
const latestTransformActivities = ref([])

const fetchLatestLibraryActivities = async () => {
  try {
    const response = await simpleGet(
      'fetchLatestLibraryActivities',
      `${functionsBaseUrl}/usersGateway/api/v1/users/agency-library-activity`
    )
    latestLibraryActivities.value = response?.data?.data || []
    console.debug('[fetchLatestLibraryActivities] loaded', latestLibraryActivities.value.length, 'activities')
  } catch (err) {
    handleErrors('fetchLatestLibraryActivities', err)
  }
}

/*
 * Agency-wide Transform activity for the Latest Activity card. Served by the gateway
 * because firestore.rules scope client reads of bcst_lesson_progress to the caller's own
 * documents, so this cannot be a direct Firestore query like the rest of Transform.
 */
const fetchLatestTransformActivities = async () => {
  try {
    const response = await simpleGet(
      'fetchLatestTransformActivities',
      `${functionsBaseUrl}/transformLearningGateway/api/v1/agency-lesson-activity`
    )
    latestTransformActivities.value = response?.data?.data || []
  } catch (err) {
    handleErrors('fetchLatestTransformActivities', err)
  }
}

const fetchRecentLibraryItems = async () => {
  try {
    const snapshot = await dbGet(dbRef(db, 'CONTENT_LIBRARY/RECENT_UPDATES'))
    if (snapshot.exists()) {
      const val = snapshot.val()
      const items = Array.isArray(val) ? val : Object.values(val)
      recentLibraryItems.value = items
        .filter(item => item?.description)
        .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
        .slice(0, 4)
        .map(({ title, date, description }) => ({ title, date, description }))
      console.debug('[fetchRecentLibraryItems] loaded', recentLibraryItems.value.length, 'items')
    } else {
      recentLibraryItems.value = []
      console.debug('[fetchRecentLibraryItems] no data at CONTENT_LIBRARY/RECENT_UPDATES')
    }
  } catch (err) {
    handleErrors('fetchRecentLibraryItems', err)
  }
}

onMounted(async () => {
  setupModalEventListeners()
  checkMobileView()
  await checkRenewAnnouncementStatus()
  await checkFeatureAnnouncementStatus()

  window.addEventListener('resize', checkMobileView)
  await fetchGlossaryData()
  // Listen for open event via mitt instead of Bootstrap event
  emitter.on('open-create-update-employer-modal', openCreateUpdateHandler)
  if (sessionStorage.getItem('userAlreadyEnterInCalendar') === null) {
    sessionStorage.setItem('userAlreadyEnterInCalendar', false)
  }
  initializeTooltips()
  // Permissions load async and hasContentLibrary is false until they do, so resolve them
  // before gating the fetch — otherwise the card renders empty for users who do have
  // Content Library access, with nothing to retry the call.
  await authStore.ensureAccessLoaded()
  if (hasContentLibrary.value) {
    fetchRecentLibraryItems()
  }
})

const initializeTooltips = () => {
  setTimeout(() => {
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      new bootstrap.Tooltip(tooltipTriggerEl, {
        trigger: 'hover'
      })
    })
  }, 200)
}

onBeforeUnmount(() => {
  emitter.off('open-renew-employer-modal', hideRenewModalOnOpenHandler)
  emitter.off('open-create-update-employer-modal', openCreateUpdateHandler)
  emitter.off('action-center:event-clicked', handleEventClicked)
})

const loadProfilePhoto = async () => {
  try {
    const photoURL = await getImageUrl('profile-photos', `${uid}.png`)
    if (photoURL) {
      profilePhotoUrl.value = photoURL
    }
  } catch (error) {
    handleErrors('Error loading profile photo:', error, { sendToSentry: false, logToConsole: false })
  }
}

const getAgencyLogoPath = async (agencyId, logoSize) => {
  try {
    return await getImageUrl('agencies-logos', `${agencyId}_${logoSize}.png`)
  } catch (error) {
    handleErrors('Error getting agency url logo:', error, { sendToSentry: false, logToConsole: false })
    return ''
  }
}

watch(
  () => customNavigation.value.currentPage,
  async (newVal, oldVal) => {
    if (isLoggingOut.value) return

    // Close ActionCenterOffCanvas when navigation changes
    if (actionCenterOffCanvasRef.value && oldVal !== newVal) {
      actionCenterOffCanvasRef.value.close()
    }

    window.scrollTo({ top: 0, behavior: 'instant' })
    if (newVal === customNavigation.value.routes.Home.name) {  
      if (showAnnouncement.value) {
       await closeRenewAnnouncement()
      }
      duplicateEmployerMatch.value = null
      searchActive.value = false
      resetQuestionStore()
      resetAllEmployerStore()
      await refreshHomeData({ showLoading: true })
    } else if (newVal === customNavigation.value.routes.ListOfEmployers.name) {
      getEmployerList()
      getAllEmployers()
      resetAllEmployerStore()
      resetQuestionStore()
    } else if (newVal === customNavigation.value.routes.ActionCenter.name) {
      resetAllEmployerStore()
      resetQuestionStore()
    }
    initializeTooltips()
    if (typeof window._mfq !== 'undefined') {
      // Notify to Mouseflow about route change with page title
      window._mfq.push(['newPageView', getPageTitle(newVal)])
    }
  }
)
watch([employerAmountToShow, orderBySelected], () => {
  getEmployerList()
})

watch(refNavigate, () => {
  searchActive.value = false
})

async function getUserData(showLoading = true, { forceRefresh = false } = {}) {
  if (showLoading) {
    pushLoading()
  }

  try {
    const user = await authStore.fetchUserProfile({ force: forceRefresh })

    if (user) {
      userData.value = {
        ...user,
        ...userData.value,
        employers: user.employers || [],
        totalRiskFormatted: formatter.format(
          Math.round((user.totalRisk || 0) / 100) * 100
        ),
        creationTime: userMetadata.value.creationTime,
        lastLogin: userMetadata.value.lastLogin,
        profilePhotoUrl: profilePhotoUrl.value,
        logoPath: userData.value?.logoPath || ''
      }
    }
    const parallelTasks = [
      getAgencyLogoPath(userData.value?.agencyId, userData.value?.logoSize)
    ]

    if (canCallApi(API_CAPABILITY.CORE_WORKBENCH)) {
      parallelTasks.push(
        simpleGet(
          'getEmployersInProgress',
          `${functionsBaseUrl}/employerGateway/api/v1/employers/in-progress`
        ),
        getEmails(userData.value?.agencyId)
      )
    }

    const results = await Promise.all(parallelTasks)
    const inProgressResponse = canCallApi(API_CAPABILITY.CORE_WORKBENCH) ? results[1] : null
    const logoPath = results[0]
    const agencyEmails = canCallApi(API_CAPABILITY.CORE_WORKBENCH) ? results[2] : []

    if (inProgressResponse) {
      inProgress.value = inProgressResponse.data?.data?.count || 0
    }

    userData.value.agencyEmails = agencyEmails || []
    userData.value.logoPath = logoPath
  } finally {
    if (showLoading) {
      popLoading()
    }
  }
}

async function getEmployerList(showLoading = true) {
  await authStore.ensureAccessLoaded()
  if (!canCallApi(API_CAPABILITY.CORE_WORKBENCH)) {
    return
  }
  if (showLoading) {
    pushLoading()
  }

  // try/finally keeps the loading counter balanced: a throw between push and pop
  // (e.g. while enriching employers) would otherwise pin the overlay forever.
  try {
    const getResponse = await simpleGet(
      'getEmployerList',
      `${functionsBaseUrl}/employerGateway/api/v1/employers?pageSize=${employerAmountToShow.value}&sortField=${orderBySelected.value.sortField}&sortDirection=${orderBySelected.value.sortDirection}`
    )
    if (getResponse) {
      employerList.value = getResponse.data?.data || []
      employerTotalCount.value = getResponse.data?.totalCount || 0

      // Enrich employers with upcomingEvents fields
      addUpcomingEventsToEmployers(employerList.value)
    }
  } finally {
    // Only modify loading state if showLoading is true
    if (showLoading) {
      popLoading()
    }
  }
}

async function getAllEmployers() {
  await authStore.ensureAccessLoaded()
  if (!canCallApi(API_CAPABILITY.CORE_WORKBENCH)) {
    return
  }
  const getResponse = await simpleGet(
    'getEmployerList',
    `${functionsBaseUrl}/employerGateway/api/v1/employers?pageSize=1000`
  )
  if (getResponse) {
    allEmployersList.value = getResponse.data?.data || []
    // Enrich with upcoming events so both lists share same attributes
    addUpcomingEventsToEmployers(allEmployersList.value)
  }
}

function increaseEmployerAmountToShow() {
  const newValue = employerAmountToShow.value + 10
  if (newValue > employerTotalCount.value) {
    employerAmountToShow.value = employerTotalCount.value
  } else {
    employerAmountToShow.value = newValue
  }
}

function sortEmployers(selectedOption) {
  orderBySelected.value = selectedOption
}

// const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     return date.toLocaleDateString('es-ES', {
//         day: '2-digit',
//         month: 'short',
//         year: 'numeric'
//     });
// };

function toggleLoadingWithMessage(isOn, message) {
  if (isOn) {
    pushLoading()
  } else {
    popLoading()
  }
  messageForLoading.value = message
}

provide('toggleLoadingWithMessage', toggleLoadingWithMessage)

function fillFormWithEmployerData() {
  // Set form values directly to the refs
  name.value = actualEmployer.value.name
  ein.value = actualEmployer.value.ein
  plan_start_date.value = actualPlanYear.value.plan_start_date
  contact_name.value = actualEmployer.value.contact_name
  contact_emails.value = actualEmployer.value.contact_emails
  accountOwners.value = getAccountOwners(actualEmployer.value)
  
  // Reset form validity since we've filled in valid data
  hasEmptyValues.value = false
  forceUpdate.value = !forceUpdate.value
}

function cleanFormNewUser() {
  // Reset form values directly to the refs
  name.value = ''
  ein.value = ''
  plan_start_date.value = ''
  contact_name.value = ''
  contact_emails.value = ''
  accountOwners.value = []
  
  // Reset duplicate employer check
  checkDuplicateEmployer.value = false
  
  // Reset form validity
  hasEmptyValues.value = true
  forceUpdate.value = !forceUpdate.value
  
  // Clean up modal backdrop if needed
  //document.querySelectorAll(".modal-backdrop").forEach(el => el.remove());
}

async function prepareForCreateEmployer() {
  isSubmitting.value = true
  const fechaActual = new Date()
  try {
    const newEmployer = await createEmployer({
      name: name.value,
      accountOwners: accountOwners.value,
      ein: ein.value,
      agency_id: userData.value.agencyId,
      plan_start_date: plan_start_date.value,
      contact_name: contact_name.value,
      contact_emails: contact_emails.value,
      creation_date: formatDateToSQLInChicagoTime(fechaActual)
    })
    // close modal after success
    CreateUpdateEmployerModalRef.value?.closeModal()
    if (newEmployer) {
      isNewEmployerAdded.value.state = true
      isNewEmployerAdded.value.id = newEmployer.id
    }
    await getEmployerList(false)
    await getAllEmployers()
  } catch (error) {
    handleErrors('Error creating employer:', error)
  } finally {
    isSubmitting.value = false
  }
}


const checkRenewAnnouncementStatus = async () => {
  try {
    if (uid) {
      const announcementRef = dbRef(db, `TEMP/ANNOUNCEMENTS/RENEW_PLAN_YEAR_VIEW/${uid}`)
      const snapshot = await dbGet(announcementRef)
      
      if (!snapshot.exists()) {
        showAnnouncement.value = true
        await dbSet(announcementRef, {
          viewedAt: new Date().toISOString(),
          uid: uid
        })
      } else {
        showAnnouncement.value = false
      }
    }
  } catch (error) {
    handleErrors('Error checking announcement status:', error)
    showAnnouncement.value = true
  }
}

const closeRenewAnnouncement = async () => {
  showAnnouncement.value = false
}

const checkFeatureAnnouncementStatus = async () => {
  try {
    const announcementRef = dbRef(db, 'TEMP/ANNOUNCEMENTS/ACTION_CENTER/enabled')
    const snapshot = await dbGet(announcementRef)
    showFeatureAnnouncement.value = snapshot.exists() && snapshot.val() === true
  } catch (error) {
    handleErrors('Error checking feature announcement status:', error)
    showFeatureAnnouncement.value = false
  }
}

const dismissFeatureAnnouncement = () => {
  showFeatureAnnouncement.value = false
}

const goToFaqs = () => {
  const faqScrollTarget = isContentLibraryOnlyUser.value ? 'contentLibrary' : 'faq-section-actionCenter'
  customNavigation.value.navigate({
    newPage: customNavigation.value.routes.frequentlyQuestions.name,
    params: { faqScrollTarget }
  })
}

const goToActionCenter = () => {
  customNavigation.value.navigate({
    newPage: customNavigation.value.routes.ActionCenter.name
  })
}

const refreshHomeData = async ({ showLoading = true, refreshAll = false, forceRefresh = true }) => {
  if (showLoading) {
    pushLoading()
  }
  try {
    const promises = [getUserData(false, { forceRefresh })]
    if (refreshAll) {
      promises.push(loadProfilePhoto())
      promises.push(getCurrentUserMetadata())
    }
    await Promise.all(promises)

    if (hasCoreWorkbench.value) {
      getAllDistinctEmployers()
    }
    // Each Latest Activity tab is fetched only for the module the user actually holds, so
    // neither persona fires a request it has no permission to read. Both run in parallel for
    // a user who has both. Permissions are already resolved here (onBeforeMount awaits
    // ensureAccessLoaded above), unlike onMounted.
    if (showLibraryStyleHome.value) {
      await Promise.all([
        hasContentLibrary.value ? fetchLatestLibraryActivities() : null,
        hasTransform.value ? fetchLatestTransformActivities() : null
      ].filter(Boolean))
    }
  } catch (error) {
    handleErrors('Error in refreshHomeData:', error)
  } finally {
    if (showLoading) {
      popLoading()
    }
  }
}

async function prepareForUpdateEmployer() {
  isSubmitting.value = true
  try {
    const updatedEmployer = await updateEmployer(actualEmployer.value.id, {
      id: actualEmployer.value.id,
      name: name.value,
      accountOwners: accountOwners.value,
      ein: ein.value,
      agency_id: userData.value.agencyId,
      plan_start_date: plan_start_date.value,
      complianceYearId: actualPlanYear.value.id,
      contact_name: contact_name.value,
      contact_emails: contact_emails.value
    })
    // close modal after success
    CreateUpdateEmployerModalRef.value?.closeModal()
    if (updatedEmployer) {
      changePlanStartDateCopy(plan_start_date.value)
      await refreshHomeData({ showLoading: false })
      const EMPLOYER_INFORMATION_UPDATED = '8'
      createLog({
        eventId: EMPLOYER_INFORMATION_UPDATED,
        logName: 'user_activity_log_v2',
        employerId: actualEmployer.value.id,
        planYearId: actualPlanYear.value.id,
        agencyId: actualEmployer.value.agency_id
      }).catch((err) => handleErrors('createLog employer update', err))
    }
  } catch (error) {
    handleErrors('Error updating employer:', error)
  } finally {
    isSubmitting.value = false
  }
}

async function openRenewEmployerModal() {
  emitter.emit('open-renew-employer-modal')
}

// // Function to recalculate total compliance risk and form states for all plan years
// async function processAllEmployers() {
//   toggleLoadingWithMessage(true, 'Processing all employers...')
//   const planYearsIdsWithAgencyIdZero = [19, 20, 23, 24, 47, 66, 67, 76, 81, 109, 111, 122, 123]
//   // define the employers plan years ids to process
//   for (let i = 1; i <= 300; i++) {
//   // for (let i of [1, 2, 3, 4]) {
//     if (planYearsIdsWithAgencyIdZero.includes(i)) {
//       continue
//     }
//     try {
//       customNavigation.value.navigate({
//         newPage: customNavigation.value.routes.DetailEmployer.name,
//         subPage: customNavigation.value.routes.DetailEmployer.children[0].name,
//         params: { planYearId: i }
//       })
//       // Wait for the page to load
//       console.log(`Processing employer plan year ${i}`)
//       await new Promise(resolve => setTimeout(resolve, 2000))
//       const questionStore = useQuestionsStore()
//       const { isAssessmentComplete, loadQuestionsAndAnswers, isPlanComplete } = questionStore
//       await loadQuestionsAndAnswers()
//       if (isAssessmentComplete() || isPlanComplete()) {
//         console.log(`Employer plan year ${i} is already completed`)
//         const { saveScore } = ScoreCalculationsService()
//         await saveScore(i)
//       }
//       customNavigation.value.navigate({
//         newPage: customNavigation.value.routes.Home.name
//       })
//       await new Promise(resolve => setTimeout(resolve, 800))
//     } catch (error) {
//       console.error(`Error processing employer plan year${i}:`, error)
//     }
//   }
//   toggleLoadingWithMessage(false)
// }


const handleSearch = ({ results }) => {
  searchActive.value = true
  searchResults.value = results  
}

const handleClear = () => {
  searchActive.value = false
}

const clearTableSearch = () => {
  searchActive.value = false
  if (searchDropdownRef.value) {
    searchDropdownRef.value.clearSearch()
  }
}

function isNameTaken() {
  checkDuplicateEmployer.value = false
  const existingEmployer = allDistinctEmployers.value.find(employer => 
    employer.name.trim().toLowerCase() === name.value.trim().toLowerCase()
  )
  if (existingEmployer) {
    duplicateEmployerMatch.value = existingEmployer
  }  
  checkDuplicateEmployer.value = !!existingEmployer
}

const continueEditEmployer = () => {
  checkDuplicateEmployer.value = false
}

const goToExistingEmployer = () => {  
  event.preventDefault()
  if (!get(actualEmployer, 'value.id')) {
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.DetailEmployer.name,
      subPage: customNavigation.value.routes.DetailEmployer.children[0].name,
      params: { planYearId: get(duplicateEmployerMatch.value, 'planYearId') }
    })
  } else {
    emitter.emit(PLAN_YEAR_EVENTS.CHANGE_PLAN_YEAR, { planYearId: get(duplicateEmployerMatch.value, 'planYearId') })
  }
}


// Navbar
const navItems = computed(() => (
  navbarLinks.value.map((link) => ({
    id: link.routeName,
    label: link.label,
    target: link.routeName
  }))
))

const activeNavItem = computed(() => {
  const currentPage = customNavigation.value.currentPage
  const routes = customNavigation.value.routes
  if (currentPage === routes.Home.name) {
    return routes.Home.name
  }
  if (currentPage === routes.ListOfEmployers.name ||
      currentPage.includes(routes.DetailEmployer.name.slice(0, 14))) {
    return routes.ListOfEmployers.name
  }
  if (currentPage === routes.ActionCenter.name) {
    return routes.ActionCenter.name
  }
  if (currentPage === routes.contentLibrary.name ||
      currentPage === customNavigation.value.routes.contentLibraryEmbeddedTool.name ||
      currentPage === customNavigation.value.routes.bcsContentLibraryTaxonomyItems.name) {
    return routes.contentLibrary.name
  }
  // Transform and FAQ views group under Help & Resources for core users; non-core
  // users have their own navbar entries instead, so highlight those directly.
  if (currentPage === routes.transformLearningDashboard.name ||
      currentPage === routes.transformModuleDetail.name ||
      currentPage === routes.transformLessonDetail.name ||
      currentPage === routes.transformSearchResults.name ||
      currentPage === routes.transformQuizResultsBreakdown.name) {
    return hasCoreWorkbench.value
      ? routes.helpAndResources.name
      : routes.transformLearningDashboard.name
  }
  if (currentPage === routes.frequentlyQuestions.name) {
    return hasCoreWorkbench.value
      ? routes.helpAndResources.name
      : routes.frequentlyQuestions.name
  }
  if (currentPage === routes.helpAndResources.name ||
      currentPage === routes.glossaryTerms.name) {
    return routes.helpAndResources.name
  }
  if (currentPage === routes.AdminCenter.name) {
    return routes.AdminCenter.name
  }
  return ''
})

// "Help" opens Help & Resources, which is core-gated — hide it for non-core users.
const profileItems = computed(() => [
  {
    id: 'profile',
    label: 'Profile',
    target: 'profile',
    type: 'dropdown'
  },
  ...(hasCoreWorkbench.value
    ? [{
        id: 'help',
        label: 'Help',
        target: 'help',
        type: 'dropdown'
      }]
    : []),
  {
    id: 'logout',
    label: 'Log Out',
    target: 'logout',
    type: 'dropdown'
  }
])

const handleNavClick = (item) => {
  customNavigation.value.navigate({
    newPage: item.target
  })
}

const handleLogoClick = () => {
  customNavigation.value.navigate({
    newPage: customNavigation.value.routes.Home.name
  })
}

const handleProfileClick = (item) => {
  if (item.id === 'profile') {
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.memberProfile.name
    })
  } else if (item.id === 'help') {
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.helpAndResources.name
    })
  }
}

const handleLearnMore = async () => {
  await nextTick()
  if (offcanvasRef.value) {
    offcanvasRef.value.openOffCanvas(
      'limitedAccessOffcanvas',
      'definition',
      undefined,
      false,
      'home-limited-access',
      null
    )
  }
}

function openCreateUpdateEmployerModal() {
  if (CreateUpdateEmployerModalRef.value) {
    CreateUpdateEmployerModalRef.value.openModal()
  }
}

function openCreateEmployerModal() {
  cleanFormNewUser()
  openCreateUpdateEmployerModal()
}

function onCloseCreateUpdateModal() {
  cleanFormNewUser()
}

function onSubmitCreateUpdateEmployer() {
  if (actualEmployer.value) {
    prepareForUpdateEmployer()
  } else {
    prepareForCreateEmployer()
  }
}

emitter.on('toggleLoading', (value) => {
  if (value.value) {
    pushLoading()
  } else {
    popLoading()
  }
})
emitter.on('action-center:event-clicked', handleEventClicked)
</script>

<template>
  <div v-if="!isMobile">

  <div id="loading-overlay" class="bg-white loading-overlay" v-if="loading"
    :style="`--bs-bg-opacity: ${messageForLoading ? '0.95' : '0.5'}`">
    <div v-if="messageForLoading">
      <div class="text-center header-24 mb-2">
        <med>{{ messageForLoading.title }}</med>
      </div>
      <div class="text-center text-16 white-space-pre-wrap">{{ messageForLoading.body || messageForLoading.description }}</div>
    </div>
    <div v-else class="spinner-border" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <NavBar
  :logoSrc="brandLogo"
  logoAlt="bcs logo"
  :activePage="activeNavItem"
  :userData="userData"
  navMode="router"
  navStyle="app"
  :navItems="navItems"
  :actionItems="profileItems"
  @nav-click="handleNavClick"
  @action-click="handleProfileClick"
  @logo-click="handleLogoClick"
  @logout="logout"
/>



  <!-- to move this section to an external component!-->
  <div v-if="customNavigation.currentPage === customNavigation.routes.Home.name">
    <template v-if="isAccessLoaded">
      <LimitedAccessBadge
        v-if="showLibraryStyleHome"
        message="You have limited access to some BCS features, due to permission settings in your company's account."
        @learnMoreClick="handleLearnMore"
      />
      <div class="bcs-page-padding">
        <div class="row d-flex justify-content-center">
        
          <template v-if="showFullWorkbenchHome">
            <div v-if="showAnnouncement" class="announcement-container">
              <AlertNotification  :showAlert="showAnnouncement"
              @update:showAlert="closeRenewAnnouncement" :showCloseButton="true" typeAlert="announcement">
                <BcsDynamicText text="renewPlanYearAnnouncement" />
              </AlertNotification>
            </div>

            <div class="feature-announcement-container">
              <AlertNotification
                :showAlert="showFeatureAnnouncement"
                @update:showAlert="dismissFeatureAnnouncement"
                :showCloseButton="true"
                typeAlert="feature"
              >
                <div class="d-flex align-items-start gap-3">
                  <img :src="megaphoneIcon" alt="" class="feature-announcement-icon" />
                  <div class="pt-3 px-2 pb-2">
                    <BcsDynamicText text="homeFeatureAnnouncementContent" />
                    <div class="d-flex align-items-center gap-3 mt-2">
                      <button class="btn btn-info" @click="goToFaqs">Visit FAQs for Walkthroughs</button>
                      <div @click="goToActionCenter" class="d-flex align-items-center text-primary fw-bold hand-cursor">
                        Go to Book Overview &amp; Action Center <i class="bi bi-chevron-right"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </AlertNotification>
            </div>
          </template>
        
          <div class="col-12 text-left ps-3 m-4.25 text-28 text-secondary">
            <med>Hello, {{ userData.fullname }}</med>
            <!-- <button class="btn btn-primary ms-3" @click="processAllEmployers">Process All Employers</button> -->
          </div>
        </div>
        <!-- The 7.5 column next to My Profile only renders for the full workbench (My Stats)
             or for a Content Library user (Recent Library Updates). A transform-only user has
             neither, so centering would leave My Profile floating mid-row — left-align the
             library-style home instead. -->
        <div
          class="row d-flex ps-0.5 pe-2.5"
          :class="showLibraryStyleHome ? 'justify-content-start' : 'justify-content-center'">

          <div class="col-12 col-md-custom-4-5 pe-1.5">
            <div class="card">
              <div class="card-header px-4 py-2.5 bg-light d-flex justify-content-between align-items-center">
                <h5 class="card-title add-teamates-link header-22">
                  <strong>My Profile</strong>
                </h5>
                <a @click="
                  customNavigation.navigate({
                    newPage: customNavigation.routes.memberProfile.name
                  })" class="view-full-profile-link hand-cursor text-decoration-none text-14 text-primary">
                  <strong id="goToFullProfileHome">
                    Go to Full Profile
                    <i class="bi bi-chevron-right ms-2"></i>
                  </strong>
                </a>
              </div>
              <div class="card-body pt-4 mt-2 px-4 internal-round-border">
                <div class="row">
                  <div class="col-4 col-md-12 col-lg-4">
                    <div class="text-center mb-2">
                      <img :src="profilePhoto" alt="profile img" :class="[
                        'profile-img img-fluid',
                        (userData.temporaryPhotoURL && userData.temporaryPhotoURL !== '/user_silhouette.png') || userData.profilePhotoUrl ? 'rounded-circle' : ''
                      ]" v-if="profilePhoto !== null" />
                      <img src="/user_silhouette.png" alt="User Silhouette" class="img-fluid user-silhouette" v-else />
                    </div>
                  </div>
                  <div style="margin-top: -0.25rem" class="col-8 col-md-12 col-lg-8">
                    <div class="row">
                      <div class="col-5 col-xxl-4">
                        <p class="my-profile-label text-14">
                          <strong>Name</strong>
                        </p>
                      </div>
                      <div class="col-7 ps-0 col-xxl-8">
                        <p class="mb-0 text-14">
                          {{ userData.fullname }}
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5 col-xxl-4">
                        <p class="my-profile-label text-14">
                          <strong>Title</strong>
                        </p>
                      </div>
                      <div class="col-7 ps-0 col-xxl-8">
                        <p class="mb-0 text-14">
                          {{ userData.role || 'N/a' }}
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5 col-xxl-4">
                        <p class="my-profile-label text-14">
                          <strong>Company</strong>
                        </p>
                      </div>
                      <div class="col-7 ps-0 col-xxl-8">
                        <p class="mb-0 text-14">
                          {{ userData.companyName }}
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5 col-xxl-4">
                        <p class="mb-2 text-14">
                          <strong>Email</strong>
                        </p>
                      </div>
                      <div class="col-7 ps-0 col-xxl-8">
                        <p class="mb-0 text-14">
                          {{ userData.email }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="showFullWorkbenchHome" class="col-12 col-md-custom-7-5 pe-0 ps-3">
            <div class="card">
              <div class="card-header ps-4 pt-2 pb-2.5 bg-light">
                <h5 class="card-title add-teamates-link header-22 text-secondary">
                  <strong>My Stats</strong>
                </h5>
              </div>
              <div class="card-body text-center ps-4.5 pe-4 pb-3 pt-3.5">
                <div class="row">
                  <div class="col-sm">
                    <div class="d-flex justify-content-center align-items-end">
                      <h1
                        class="stat-data-pill text-primary my-auto mb-2.5 pt-1 px-3.5 bg-light rounded-pill header-40 text-dark">
                        <strong>{{ inProgress }}</strong>
                      </h1>
                    </div>
                    <h5 class="mb-0 text-14">
                      <strong>Employers in Progress</strong>
                    </h5>
                    <p class="text-12 mt-2 mb-0">
                      <BcsDynamicText :key="inProgress" text="employersInProgressText"
                        :variables="{ employersInProgress: inProgress }" />
                    </p>
                  </div>
                  <div class="col-sm">
                    <div class="d-flex justify-content-center align-items-end">
                      <h1
                        class="stat-data-pill text-primary my-auto mb-2.5 pt-1 px-3.5 bg-light rounded-pill header-40 text-dark">
                        <strong>{{ userData.employerCount || 0 }}</strong>
                      </h1>
                    </div>
                    <h5 class="mb-0 text-14">
                      <strong>Total Employers</strong>
                    </h5>
                    <p class="text-12 mt-2 mb-0">
                      <BcsDynamicText :key="userData.employerCount" text="totalEmployersText"
                        :variables="{ totalEmployers: userData.employerCount || 0 }" />
                    </p>
                  </div>
                  <div class="col-sm">
                    <div class="d-flex justify-content-center align-items-end">
                      <h1
                        class="stat-data-pill text-primary my-auto mb-2.5 px-3.5 pt-1 bg-light rounded-pill header-40 text-dark">
                        <strong>{{ userData.totalRiskFormatted || 0 }}</strong>
                      </h1>
                    </div>
                    <h5 class="mb-0 text-14">
                      <strong>Total Compliance Risk Amount</strong>
                    </h5>
                    <p class="text-12 mt-2 mb-0">
                      <BcsDynamicText v-if="Number(userData.totalRisk) !== 0" :key="`${userData.totalRiskFormatted}-${userData.completedAssessmentEmployerCount || 0}`"
                        text="totalComplianceRiskIdentifiedText" :variables="{
                          totalComplianceRiskIdentifiedValue: userData.totalRiskFormatted,
                          completedAssessmentEmployerCount: userData.completedAssessmentEmployerCount || 0,
                          pluralSuffix: (userData.completedAssessmentEmployerCount || 0) === 1 ? '' : 's'
                        }" />
                      <span v-else>
                        Start identifying the total compliance risk of your book by completing a
                        Compliance Assessment on one of your employer groups today
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="showLibraryStyleHome && hasContentLibrary" class="col-12 col-md-custom-7-5 pe-0 ps-3">
            <RecentLibraryUpdates
              title="Recent Library Updates"
              :items="recentLibraryItems"
            />
          </div>
        </div>
        <div v-if="showFullWorkbenchHome" class="row d-flex justify-content-center mt-20 ps-1.5 pe-2.5">
          <div class="col-md-12 pe-0 ps-2">
            <div class="card">
              <div
                class="card-header card-header-activity bg-light ps-4 pe-4 d-flex justify-content-between align-items-center">
                <h5 class="card-title add-teamates-link header-22 text-secondary">
                  <strong>Latest Activity</strong>
                </h5>
                <a id="goToAllEmployersLink" class="view-full-profile-link hand-cursor text-decoration-none text-14 text-primary" @click.prevent="
                  customNavigation.navigate({
                    newPage: customNavigation.routes.ListOfEmployers.name
                  })
                  " href="#">
                  <strong>Go to All Employers</strong>
                  <i class="bi bi-chevron-right ms-2"></i>
                </a>
              </div>
              <div class="card-body pt-0 px-0 pb-0">
                <div class="table-responsive">
                  <table class="table mb-1 border-0" v-if="userData.employers?.length > 0">
                    <thead>
                      <tr>
                        <th scope="col" class="text-16 ps-4">
                          <strong>Plan Year</strong>
                        </th>
                        <th scope="col" class="text-16">
                          <strong>Employer Name</strong>
                        </th>
                        <th scope="col" class="text-16">
                          <strong>Latest Activity</strong>
                        </th>
                        <th scope="col" class="text-16">
                          <strong>Activity Date</strong>
                        </th>
                        <th scope="col" class="text-16">
                          <strong>Team Member</strong>
                        </th>
                        <th scope="col" class="text-16">
                          <strong>Title</strong>
                        </th>
                        <th scope="col" class="text-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr class="" v-for="(employer, i) in userData.employers" :key="i">
                        <td class="border border-0 ps-4 text-14 text-secondary py-0">
                          {{ employer.plan_start_date }}
                        </td>
                        <td class="border border-0 text-truncate" style="max-width: 200px;">
                          <a @click.prevent="
                            customNavigation.navigate({
                              newPage: customNavigation.routes.DetailEmployer.name,
                              subPage: customNavigation.routes.DetailEmployer.children[0].name,
                              params: { planYearId: employer.planYearId }
                            })
                            " href="#" class="text-primary text-decoration-none text-14 text-primary hand-cursor">
                            <strong>{{ employer.name }}</strong>
                          </a>
                        </td>
                        <td class="border border-0 py-0 text-truncate" style="max-width: 200px;">
                          <span
                            class="d-inline-block duedate-pill rounded-pill bg-light px-2 text-14 text-secondary text-decoration-none">
                            <strong>
                              {{ employer.log_entry?.eventDescription || 'N/a' }}
                            </strong>
                          </span>
                        </td>
                        <td class="border border-0 text-14 text-secondary py-0">
                          {{ employer.log_entry?.activityDate || 'N/a' }}
                        </td>
                        <td class="border border-0 py-0 text-truncate" style="max-width: 150px;">
                          <a class="text-14 text-secondary text-decoration-none" style="position: relative; top: -0.85px;">{{ employer.log_entry?.teamMemberName || 'N/a' }}</a>
                        </td>
                        <td class="border border-0 text-14 text-secondary py-0 text-truncate" style="max-width: 150px;">
                          {{ 'N/a' }}
                        </td>
                        <td class="border border-0 py-0 pe-4 text-end">
                          <button @click="
                            customNavigation.navigate({
                              newPage: customNavigation.routes.DetailEmployer.name,
                              subPage: customNavigation.routes.DetailEmployer.children[0].name,
                              params: { planYearId: employer.planYearId }
                            })
                            " class="hand-cursor btn btn-primary px-2 py-0.5 gotoEmployerBtn">
                            <i class="bi bi-chevron-right text-white"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table class="table mb-1" style="min-height: 200px" v-else>
                    <tbody>
                      <tr v-if="!loading">
                        <td colspan="7" class="text-center">
                          <div>
                            <i class="bi bi-plus-circle fs-1 mt-4 opacity-25"></i>
                          </div>

                          <div id="emptyLatestAgencyActivityText" class="text-14 text-secondary">
                            <BcsDynamicText text="emptyLatestAgencyActivityText" />
                          </div>
                          <div>
                            <button class="btn btn-primary px-2 py-1 mt-3 mb-4 fw-bold" @click="openCreateEmployerModal">
                              <i class="bi bi-plus-circle-fill"></i>
                              Add Employer
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="showLibraryStyleHome" class="row d-flex justify-content-center mt-20 ps-1.5 pe-2.5">
          <div class="col-md-12 pe-0 ps-2 mt-20">
            <LatestActivityTable
              :latestLibraryActivities="latestLibraryActivities"
              :latestTransformActivities="latestTransformActivities"
              :hasContentLibrary="hasContentLibrary"
              :hasTransform="hasTransform"
            />
          </div>
        </div>
      
        <!-- The limited-access Home drops the ": {sectionName}" suffix: the persona already
             implies the module, and with two sections the single name would be misleading. -->
        <Faqs :section="homeFaqSections" :showSectionName="!showLibraryStyleHome"></Faqs>
      </div>
      <Footer class="main-container"></Footer>
    </template>
  </div>

  <div v-else-if="customNavigation.currentPage === customNavigation.routes.ListOfEmployers.name">
    <div class="bcs-page-padding">
      <div class="row d-flex justify-content-center">
        <div class="col-12 text-left my-3 text-28">
          <med id="allEmployersDashboardTitle" class="text-secondary">All Employers Dashboard</med>
        </div>
      </div>
      <div class="row d-flex justify-content-center mt-0">
        <div class="col-md-12">
          <div class="card border-0">
            <div class="card-body pt-0 px-0" v-if="userData.employers">
              <div class="d-flex justify-content-between py-3">
                <div class="d-flex gap-2">
                  <button id="addNewEmployerButton" type="button" class="btn btn-primary d-flex align-items-center" @click="openCreateEmployerModal">
                    <i
                      class="bi bi-plus bg-white text-primary rounded-circle d-flex justify-content-center align-items-center fs-5"></i>
                    &nbsp; Add New Employer
                  </button>
                  <button type="button" id="renewalEmployerButton" class="btn btn-info d-flex align-items-center" @click="openRenewEmployerModal"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Create a new plan year for an employer that already exists in the system.">
                    <i class="bi bi-calendar-plus-fill me-1"></i> <span class="text-16"><med>Renew an Employer</med></span>
                  </button>
                </div>
                <div class="d-flex align-items-center">
                  <div style="padding-right: 26px;">
                    <BcsSearchDropdown custom-class="search-employer" ref="searchDropdownRef" @clear="handleClear" @search="handleSearch"
                      :search-fields="['name']" :showResults="false" :placeholder="'Search Employer by Name'"
                      :items="allEmployersList" :search-button-text="''" />
                  </div>
                  <div :class="{'text-gray-500': searchActive}" class="mb-0 me-3">Sort by</div>
                  <div class="dropdown" :class="{'dropdown-btn-disabled': searchActive}">
                    <button :disabled="searchActive" type="button" class="btn btn-outline-secondary btn-sort"
                      id="dropdownMenuSort" data-bs-toggle="dropdown" aria-expanded="false">
                      <span class="text-16 text-primary me-2">{{ orderBySelected.label }}</span>
                      <i class="bi bi-caret-down-fill ms-5 text-primary"></i>
                    </button>
                    <ul id="sortOptionsList" class="dropdown-menu dropdown-menu-end w-100" aria-labelledby="dropdownMenuSort">
                      <li v-for="opcion in orderOptions" :key="opcion.value" class="dropdown-item hand-cursor" :class="{
                        'bg-primary text-white': orderBySelected.value === opcion.value
                      }" @click="sortEmployers(opcion)" @keydown.enter="$event.target.click()" tabindex="0">
                        <span>
                          {{ opcion.label }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="border mb-3 rounded rounded-1 table-responsive">
                <table id="employer-table" class="table mb-1">
                  <thead>
                    <tr>
                      <th scope="col" class="py-3 rounded rounded-1 border-end-0 text-16 ps-4 text-secondary">
                        <strong>Plan Year</strong>
                      </th>
                      <th scope="col" class="py-3 rounded rounded-1 border-end-0 text-16 text-secondary">
                        <strong>Employer Name</strong>
                      </th>
                      <th scope="col" class="py-3 border-start-0 border-end-0 text-16 text-secondary">
                        <strong>Latest Activity</strong>
                      </th>
                      <th scope="col" class="py-3 border-start-0 border-end-0 text-16 text-secondary">
                        <strong>Activity Date</strong>
                      </th>
                      <th scope="col" class="py-3 border-start-0 border-end-0 text-16 text-secondary">
                        <strong>Upcoming Due Dates</strong>
                      </th>
                      <th scope="col" class="py-3 rounded rounded-1 border-start-0"></th>
                    </tr>
                  </thead>
                  <tbody v-if="!searchActive">
                    <tr v-for="(employer, i) in employerList" :key="i"
                      :class="i == employerList.length - 1 ? 'border border-0' : ''">
                      <td :class="i == employerList.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-start-0 border-end-0 text-14 text-secondary ps-4">
                        {{ employer.plan_start_date }}
                      </td>
                      <td :class="i == employerList.length - 1 ? 'border border-0' : ''" class="py-1 border-end-0 text-truncate" style="max-width: 200px;">
                        <a @click.prevent="
                          customNavigation.navigate({
                            newPage: customNavigation.routes.DetailEmployer.name,
                            subPage: customNavigation.routes.DetailEmployer.children[0].name,
                            params: { planYearId: employer.planYearId }
                          })
                          " href="#" class="text-decoration-none text-14 hand-cursor" >
                          <strong>{{ employer.name }}</strong>
                        </a>
                      </td>
                      <td :class="i == employerList.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-start-0 border-end-0" style="min-width: 210px;">
                        <span class="badge rounded-pill text-bg-light text-14 text-decoration-none text-secondary">
                          <strong>{{ employer.log_entry?.eventDescription || 'N/a' }}</strong>
                        </span>
                      </td>
                      <td :class="i == employerList.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-start-0 border-end-0 text-14 text-secondary" style="max-width: 200px;">
                        {{ employer.log_entry?.activityDate || 'N/a' }}
                      </td>
                      <td :class="i == employerList.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-start-0 border-end-0 text-14" style="max-width: 250px">
                        <template v-if="employer.upcomingEvents">
                          <BcsTooltip
                            :content="employer.upcomingEventsArray"
                            :triggerOnClick="true"
                            :isActive="activeTooltipPlanYearId === employer.planYearId"
                            :show-arrow="true"
                            :employer="employer"
                            source="all-employers-dashboard"
                            @tooltipOpen="status => handleTooltipToggle(status, employer.planYearId)"
                            @event-clicked="handleEventClicked"
                          >
                            <span class="d-inline-block text-truncate text-primary hand-cursor text-14" style="max-width: 300px">
                                <strong>{{ employer.upcomingEvents }}</strong>
                            </span>
                          </BcsTooltip>
                        </template>
                        <template v-else>
                          <span class="text-gray-600" style="max-width: 180px;">
                            {{ employer.isPlanYearExpired
                              ? 'Plan year expired - renew employer for upcoming requirements'
                              : 'Enter employer info to see due dates' }}
                          </span>
                        </template>
                      </td>
                      <td :class="i == employerList.length - 1 ? 'border border-0' : ''" @click="
                        customNavigation.navigate({
                          newPage: customNavigation.routes.DetailEmployer.name,
                          subPage: customNavigation.routes.DetailEmployer.children[0].name,
                          params: { planYearId: employer.planYearId }
                        })
                        " class="py-1 border-start-0 ps-4">
                        <button class="btn btn-primary px-2 py-1">
                          <i class="bi bi-chevron-right text-white"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr v-for="(employer, i) in searchResults" :key="i"
                      :class="i == searchResults.length - 1 ? 'border border-0' : ''">
                      <td :class="i == searchResults.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-start-0 border-end-0 text-14 text-secondary ps-4">
                        {{ employer.plan_start_date }}
                      </td>
                      <td :class="i == searchResults.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-end-0 text-truncate" style="max-width: 200px;">
                        <a @click.prevent="
                          customNavigation.navigate({
                            newPage: customNavigation.routes.DetailEmployer.name,
                            subPage: customNavigation.routes.DetailEmployer.children[0].name,
                            params: { planYearId: employer.planYearId }
                          }),
                          searchActive = false
                          " href="#" class="text-decoration-none text-14 hand-cursor">
                          <strong>{{ employer.name }}</strong>
                        </a>
                      </td>
                      <td :class="i == searchResults.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-start-0 border-end-0" style="min-width: 210px;">
                        <span class="badge rounded-pill text-bg-light text-14 text-decoration-none text-secondary">
                          <strong>{{ employer.log_entry?.eventDescription || 'N/a' }}</strong>
                        </span>
                      </td>
                      <td :class="i == searchResults.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-start-0 border-end-0 text-14 text-secondary" style="max-width: 200px;">
                        {{ employer.log_entry?.activityDate || 'N/a' }}
                      </td>
                      <td :class="i == searchResults.length - 1 ? 'border border-0' : ''"
                        class="py-1 border-start-0 border-end-0 text-14">
                        <template v-if="employer.upcomingEvents">
                          <BcsTooltip
                            :content="employer.upcomingEventsArray"
                            :triggerOnClick="true"
                            :isActive="activeTooltipPlanYearId === employer.planYearId"
                            :show-arrow="true"
                            :employer="employer"
                            source="all-employers-dashboard"
                            @tooltipOpen="status => handleTooltipToggle(status, employer.planYearId)"
                            @event-clicked="handleEventClicked"
                          >
                            <span class="d-inline-block text-truncate text-primary hand-cursor text-14" style="max-width: 300px">
                              <strong>{{ employer.upcomingEvents }}</strong>
                            </span>
                          </BcsTooltip>
                        </template>
                        <template v-else>
                          <span class="text-gray-600" style="max-width: 180px;">
                            {{ employer.isPlanYearExpired
                              ? 'Plan year expired - renew employer for upcoming requirements'
                              : 'Enter employer info to see due dates' }}
                          </span>
                        </template>
                      </td>
                      <td :class="i == searchResults.length - 1 ? 'border border-0' : ''" @click="
                        customNavigation.navigate({
                          newPage: customNavigation.routes.DetailEmployer.name,
                          subPage: customNavigation.routes.DetailEmployer.children[0].name,
                          params: { planYearId: employer.planYearId }
                        }),
                        searchActive = false
                        " class="py-1 border-start-0">
                        <button class="btn btn-primary px-2 py-1">
                          <i class="bi bi-chevron-right text-white"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>

                </table>

                <div v-if="searchActive && searchResults.length==0" style="margin-top: 64px; margin-bottom: 69px;"
                  class="text-14 d-flex flex-column align-items-center justify-content-center">
                  <p>
                    <BcsDynamicText text="searchNoResultsMessage" />
                  </p>
                  <button @click="clearTableSearch" class="btn btn-primary">
                    <i class="bi bi-x-circle-fill"></i>
                    Clear Search
                  </button>
                </div>

              </div>

              <div v-if="searchActive && searchResults.length>0" style="margin-top: 30px;"
                class="text-14 d-flex flex-column align-items-center justify-content-center">
                <p>
                  <BcsDynamicText text="searchNoResultsMessage" />
                </p>
                <p @click="clearTableSearch" style="cursor: pointer;" class="text-primary fw-bold"> <i
                    style="padding-right: 8px;" class="bi bi-x-circle-fill"></i> Clear Table Search Results </p>
              </div>

              <p v-if="!searchActive" class="text-12" style="color: var(--bs-gray-700)">
                You’re viewing {{ employerAmountToShow > employerTotalCount ? employerTotalCount : employerAmountToShow
                }} of {{ employerTotalCount }} employers
              </p>
              <button id="loadMoreEmployersButton"
                v-if="(employerAmountToShow !== employerTotalCount && employerAmountToShow < employerTotalCount) && !searchActive"
                type="button" class="btn btn-info text-16" @click="increaseEmployerAmountToShow">
                <med>Load More Employers</med>
                <i class="bi bi-chevron-double-down ms-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <Faqs :loading="loading" section="employeDashboard"></Faqs>
    </div>
    <Footer class="main-container"></Footer>
  </div>

  <ExistingEmployerDashboard v-else-if="
    customNavigation.currentPage?.includes(
      customNavigation.getDynamicRoute(customNavigation.routes.DetailEmployer.name, {
        planYearId: customNavigation.navigationParams.planYearId
      })
    )
  " :planYearId="customNavigation.navigationParams.planYearId" />

  <ActionCenter v-else-if="customNavigation.currentPage === customNavigation.routes.ActionCenter.name" />

  <BcsFaqs v-else-if="customNavigation.currentPage === customNavigation.routes.frequentlyQuestions.name" />

  <BcsGlossary v-else-if="customNavigation.currentPage === customNavigation.routes.glossaryTerms.name" />

  <ContentLibraryEmbeddedTool v-else-if="customNavigation.currentPage === customNavigation.routes.contentLibraryEmbeddedTool.name" />

  <BcsContentLibrary v-else-if="customNavigation.currentPage === customNavigation.routes.contentLibrary.name" />

  <BCSContentLibraryTaxonomyItems
    v-else-if="customNavigation.currentPage === customNavigation.routes.bcsContentLibraryTaxonomyItems.name" />

  <HelpAndResources v-else-if="customNavigation.currentPage === customNavigation.routes.helpAndResources.name && authStore.hasPermission(PERMISSION_KEYS.CORE_ACCESS)" />

  <TransformLearningDashboard
    v-else-if="customNavigation.currentPage === customNavigation.routes.transformLearningDashboard.name" />

  <TransformModuleDetail
    v-else-if="customNavigation.currentPage === customNavigation.routes.transformModuleDetail.name" />

  <TransformLessonDetail
    v-else-if="customNavigation.currentPage === customNavigation.routes.transformLessonDetail.name" />

  <TransformSearchResults
    v-else-if="customNavigation.currentPage === customNavigation.routes.transformSearchResults.name" />

  <TransformQuizResultsBreakdown
    v-else-if="customNavigation.currentPage === customNavigation.routes.transformQuizResultsBreakdown.name" />

  <AdminCenter v-else-if="customNavigation.currentPage === customNavigation.routes.AdminCenter.name && authStore.hasPermission(PERMISSION_KEYS.ADMIN_CENTER_ACCESS)" />

  <TermsAndConditions v-else-if="customNavigation.currentPage === customNavigation.routes.termsAndConditions.name"/>

  <BcsMemberProfile v-else-if="customNavigation.currentPage === customNavigation.routes.memberProfile.name" />

  <!--  -->

  <Offcanvas ref="offcanvasRef" canvas-id="definitionOffcanvas" canvas-position="end" :selected-context="selectedContext" @open-reminder-modal="handleOpenReminderModal" />
  <ActionCenterOffCanvas ref="actionCenterOffCanvasRef" />
  <CenterDefinitionModal />
  <BcsContactModal />

  <!-- createUpdateEmployerModal -->
  <CreateUpdateEmployerModal
    ref="CreateUpdateEmployerModalRef"
    :employerData="employerData"
    :checkDuplicateEmployer="checkDuplicateEmployer"
    :isNameTaken="isNameTaken"
    :goToExistingEmployer="goToExistingEmployer"
    :duplicateEmployerMatch="duplicateEmployerMatch"
    :forceUpdate="forceUpdate"
    :continueEditEmployer="continueEditEmployer"
    :actualEmployer="actualEmployer"
    :hasEmptyValues="hasEmptyValues"
    :isSubmitting="isSubmitting"
    @update="(formState) => {
      name = formState.values.name;
      accountOwners = formState.values.accountOwners || [];
      ein = formState.values.ein;
      plan_start_date = formState.values.plan_start_date;
      contact_name = formState.values.contact_name;
      contact_emails = formState.values.contact_emails;
      hasEmptyValues = !formState.isValid;
    }"
    @submit="onSubmitCreateUpdateEmployer"
    @close="onCloseCreateUpdateModal"
  />

  <!-- detailEmployerModal -->
  <div class="modal" id="detailEmployerModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
    <div class="modal-dialog" v-if="actualEmployer">
      <div class="modal-content">
        <div class="modal-header px-4 d-flex flex-column bg-gray-200">
          <div class="d-flex w-100 justify-content-between">
            <span class="modal-title header-20" id="answerModalLabel">
              <strong>Employer Details</strong>
            </span>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <p class="me-5 mb-0 text-14 text-gray-700 mt-1">
            <BcsDynamicText text="detailEmployerModalDescriptionText" />
          </p>
        </div>
        <div class="modal-body">
          <div class="row mx-3">
            <div class="col-12">
              <div class="row">
                <div class="col-sm-5 pe-0">
                  <p class="mb-2 text-truncate text-14">
                    <strong>Employer Name</strong>
                  </p>
                </div>
                <div class="col-sm-6 ps-0">
                  <p class="mb-0 text-truncate text-14">
                    {{ actualEmployer.name }}
                  </p>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-5 pe-0">
                  <p class="mb-2 text-truncate text-14">
                    <strong>EIN</strong>
                  </p>
                </div>
                <div class="col-sm-6 ps-0">
                  <p class="mb-0 text-truncate text-14">
                    {{ actualEmployer.ein }}
                  </p>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-5 pe-0">
                  <p class="mb-2 text-truncate text-14">
                    <strong>Plan Year Start</strong>
                  </p>
                </div>
                <div class="col-sm-6 ps-0">
                  <p class="mb-0 text-truncate text-14">
                    {{ actualPlanYear?.plan_start_date }}
                  </p>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-5 pe-0">
                  <p class="mb-2 text-truncate text-14">
                    <strong>Primary Contact</strong>
                  </p>
                </div>
                <div class="col-sm-6 ps-0">
                  <p class="mb-0 text-truncate text-14">
                    {{ actualEmployer.contact_name }}
                  </p>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-5 pe-0">
                  <p class="mb-2 text-truncate text-14">
                    <strong>Employer Emails</strong>
                  </p>
                </div>
                <div class="col-sm-6 text-14 ps-0">
                  <p class="mb-0 text-truncate add-teamates-link">
                    {{ actualEmployer.contact_emails }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" @click="cleanEmployerDetail" class="btn text-primary bg-info" data-bs-dismiss="modal">
            Close
          </button>
          <button type="button" class="btn btn-primary" data-bs-toggle="modal"
            data-bs-target="#createUpdateEmployerModal">
            Edit Employer Details
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- employer created  -->
  <div v-if="isNewEmployerAdded.state" class="col-12 overlay-white">
    <div id="newAddEmployerModal" class="card-new-employer mt-5 mx-auto">
      <div class="d-flex justify-content-end">
        <button type="button" class="btn-close" @click="isNewEmployerAdded.state = false" aria-label="Close"></button>
      </div>
      <div class="card-body text-center d-flex flex-column justify-content-center align-items-center">
        <p class="card-text text-14 text-gray-700">
          <i class="bi bi-check-circle"></i>
        </p>

        <strong>
          <h4>New Employer Added!</h4>
        </strong>
        <p class="text-12">
          Now that you've added {{ name || '' }}, completing the Plan Questionnaire will customize
          and optimize this employer's Assessment, Planning, and Calendar results.
        </p>

        <button @click="
          customNavigation.navigate({
            newPage: customNavigation.routes.DetailEmployer.name,
            subPage: customNavigation.routes.DetailEmployer.children[1].name,
            params: { planYearId: isNewEmployerAdded.id }
          }),
          (isNewEmployerAdded.state = false)
          " class="btn btn-primary">
          <i class="bi bi-list-check"></i>
          &nbsp; Complete Plan Questionnaire
        </button>

        <strong @click="
          customNavigation.navigate({
            newPage: customNavigation.routes.DetailEmployer.name,
            subPage: customNavigation.routes.DetailEmployer.children[0].name,
            params: { planYearId: isNewEmployerAdded.id }
          }),
          (isNewEmployerAdded.state = false)
          " class="complete-plan-later">
          I’ll complete the Plan Questionnaire later.
        </strong>
      </div>
    </div>
  </div>

  <!-- pendingFunctionModal -->
  <div class="modal fade" id="pendingFunctionModal" tabindex="-1" aria-labelledby="exampleModalLabel"
    aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5" id="exampleModalLabel">Pending functionalty</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <h2>Stay tuned for updates and new version releases</h2>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
  <RenewEmployerModal
    ref="renewEmployerModalRef"
    @refreshHomeData="refreshHomeData"
  />
  <ReminderModalVue
    ref="homeReminderModalRef"
    :filtered-events="[]"
    :modal-reminder-source="'OffCanvas'"
    :reminder-days-option="reminderDaysOption"
    :sort-direction-events="''"
    :event-definition="reminderModalProps.eventDefinition"
    :actual-employer="reminderModalProps.actualEmployer"
    :actual-plan-year="reminderModalProps.actualPlanYear"
    :recipients="userData.agencyEmails || []"
    :year-of-actual-plan="reminderModalProps.yearOfActualPlan"
    @sendReminders="handleSendReminders"
  />
  <BcsModal
    ref="reminderSentModalRef"
    modal-type="success"
    modal-title="Reminder Created!"
    :hide-close-button="false"
    :no-default-styles="true"
    modal-size="small">
    <div class="text-center d-flex flex-column justify-content-center align-items-center pb-2">
      <p class="text-16 text-gray-700">Your selected reminders were sent!</p>
      <button @click="reminderSentModalRef.closeModal()" class="btn btn-primary">
        <i class="bi bi-x-circle"></i>
        &nbsp; Close
      </button>
    </div>
  </BcsModal>
 </div>

 <div v-else>
    <MobileMessage />
 </div>
</template>

<style scoped lang="scss">
body.body--light {
  color: black;
}

.announcement-container{
  margin-top: 37px;
  margin-bottom: 14px;
}

.feature-announcement-container {
  margin-top: 0;
  margin-bottom: 0;
}

.feature-announcement-icon {
  width: 120px;
  height: 130px;
  flex-shrink: 0;
}

.q-parallax__media>img {
  min-width: 50% !important;
}

.card {
  height: 100%;
}

.hand-cursor {
  cursor: pointer;
}

.z-index-1021 {
  z-index: 1021;
}

.opacity-50 {
  opacity: 50%;
}

.opacity-25 {
  opacity: 25%;
}

.bg-gray-200 {
  background-color: var(--bs-gray-200);
}

html {
  scroll-behavior: smooth;
}

.row-adjustement {
  width: 100vw;
}

:host {
  table {
    margin-bottom: 0;
  }
}

.table-responsive>.table {
  margin-bottom: 0;
  border: 0;
}

.card-header-activity {
  height: 51px;
}

tr {
  border-bottom: 1px solid #c5cdd4;
}

tbody tr:last-child {
  border: none;
}

.loading-overlay {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
  height: 100vh;
  width: 100vw;
  --bs-bg-opacity: 0.95;
  // padding-top: 40vh;
  // padding-left: 49vw;
}

.mt-20 {
  margin-top: 2rem;
}

.card-body {
  border-radius: var(--bs-border-radius) !important;
}

.loading-overlay {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
  height: 100vh;
  width: 100vw;
  --bs-bg-opacity: 0.95;
  // padding-top: 40vh;
  // padding-left: 49vw;
}

.mt-20 {
  margin-top: 20px;
}

.card-body {
  border-radius: var(--bs-border-radius) !important;
}

.padding-logo {
  padding-right: 10%;
}

.h-61 {
  height: 61px !important;
}

.padding_icons {
  padding-top: 3px;
  padding-bottom: 3px;
}

.company_logo {
  display: flex;
  justify-content: center;
}

.btn-sort:hover {
  border-color: var(--bs-link-color-rgb);
  color: var(--bs-link-color);
  background-color: transparent;
}

.btn-sort:focus-visible {
  border-color: var(--bs-link-color-rgb);
  color: var(--bs-link-color);
  background-color: transparent;
}

.logo {
  height: 20px;
  // margin-top: 1.4vh;
}

.card {
  height: 100%;
}

.add-teamates-link {
  display: contents;
}

.n-margin-8 {
  margin-right: -30px;
}

.btn-sort:hover {
  border-color: var(--bs-link-color-rgb);
  color: var(--bs-link-color);
  background-color: transparent;
}

.footer-color {
  background-color: #e9ecef;
}


.bi-check-circle {
  font-size: 86px;
  color: #198754;
}

.duedate-pill {
  padding: 6px;
}

:deep(#renewPlanYearAnnouncementDefinition) {
    text-decoration: none;
    border-bottom: none;

    &::after{
      content: "\F285";
      font-family: "bootstrap-icons";
      display: inline-block;
      margin-left: 5px;
      vertical-align: middle;
      line-height: 1;
    }
}






.hand-cursor {
  cursor: pointer;
}

.card-body {
  border-radius: var(--bs-card-border-radius);
}

.stat-data-pill {
  height: 60px;
}

.profile-img {
  width: 118px;
}

.dropdown-btn-disabled{

  button{
    border-radius: 4px!important;
    border: 1px solid var(--Gray-400, #CED4DA)!important;
    background: var(--Gray-200, #E9ECEF)!important;

    span{
      color: var(--Gray-500, #ADB5BD)!important;
    }

    i{
      color: var(--Gray-500, #ADB5BD)!important;
    }
  }
  
}

.card-new-employer {
  padding: 18px;
  border: 1px solid #c5cdd4;
  border-radius: 4px;
  background-color: #fff;
  width: 454px;
  height: 375px;
  box-shadow: 4px 4px 8px 0px #0000001a;

  .complete-plan-later {
    color: var(--bs-primary);
    margin-top: 26px;
    font-size: 14px;
    cursor: pointer;
  }

  i {
    margin-top: 43px;
  }

  button {
    margin-top: 6px;

    .stat-data-pill {
      height: 69px;
    }

    h4 {
      font-weight: 700;
      font-size: 22px;
    }
  }

  .social-icon {
    font-size: 24px;
  }

  .dropdown-item {
    color: var(--bs-primary);
  }

  .dropdown-item:hover,
  .dropdown-item:focus {
    color: var(--bs-white);
    background-color: var(--bs-primary);
  }

  button {
    margin-top: 6px;
  }

  h4 {
    font-weight: 700;
    font-size: 22px;
  }
}

.social-icon {
  font-size: 24px;
}

:deep{
  .search-container{
    input{
      border-color: var(--bs-gray-600);
    }
  }
}

:deep{
  .search-employer{
    input{
      width: 327px;
    }
  }
}

.dropdown-item {
  color: var(--bs-primary);
}

.dropdown-item:hover,
.dropdown-item:focus {
  color: var(--bs-white);
  background-color: var(--bs-primary);
}

.user-silhouette {
  max-width: 150px;
  padding: 15px;
  border-radius: 50%;
  background-color: #ECECEC;
}

.duplicate-name-container{
  color: #521B98;

  &__message{
    gap: 12px;
  }

  &__buttons{
    margin-top: 20px;
    padding-left: 24px;
  }

  .continue-adding-button{
    margin-top: 14px;
    cursor: pointer;
  }
}

@media (min-width: 768px) {
  .col-md-custom-4-5 {
    flex: 0 0 40%;
    max-width: 40%;
  }

  .col-md-custom-7-5 {
    flex: 0 0 60%;
    max-width: 60%;
  }
}

.gotoEmployerBtn {
  border-radius: 3.2px;
  width: 31px;
  height: 31px;
}

.latest-library-activity-file-cell {
  color: var(--bs-primary);
}

.latest-library-activity-file-link {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit !important;
}

.my-profile-label {
  margin-bottom: 0.6rem;
}

</style>